import { generatePath, type NavigateFunction } from "react-router-dom";

import { SearchParams } from "@/shared/environment";
import { LoggerService } from "@/shared/services/LoggerService";
import { nanoSecToSecStr } from "@/shared/time";

import { layoutNavigationState } from "./layoutNavigation";
import {
  ActionRouteParams,
  ActionsRouteParam,
  AgentThreadRouteParams,
  CollectionRouteParams,
  CreateSessionLocationState,
  CreateSessionRouteParams,
  DatasetRouteParams,
  DeviceRouteParams,
  FileRouteParams,
  InviteRouteParams,
  InvocationRouteParams,
  InvokeActionRouteParams,
  LinkSlackRouteParams,
  SessionRouteParams,
  SignInRouteParams,
  SignUpRouteParams,
  TriggerRouteParams,
  VisualizerRouteParams,
} from "./types";

export class Goto {
  static route = Object.freeze({
    Account: "/account",
    ActionHub: "/actions/hub",
    ActionImages: "/actions/images",
    Actions: "/actions",
    Admin: "/admin",
    AgentSkills: "/agents/skills",
    AgentThreads: "/agents/threads",
    Agents: "/agents",
    Collections: "/collections",
    Comments: "/comments",
    CreateAction: "/actions/create",
    CreateAgent: "/agents/new",
    CreateDataset: "/datasets/create",
    CreateSession: "/sessions/create",
    CreateTrigger: "/triggers/create",
    Datasets: "/datasets",
    Devices: "/devices",
    Events: "/events",
    Files: "/files",
    FoxgloveIntegration: "/integrations/foxglove",
    Home: "/",
    Integrations: "/integrations",
    S3Integration: "/integrations/s3",
    SlackIntegration: "/integrations/slack",
    /**
     * Where Slack redirects the installing admin's browser. This string is one of four copies
     * that must agree, and the only one the SPA owns: the others are `OAUTH_CALLBACK_PATH` in
     * `roboto_service.domain.slack.oauth`, the `redirect_urls` entry in that package's
     * `manifest.yaml`, and the URL registered in each environment's live Slack app. Nothing
     * enforces agreement across the language boundary — drift shows up only as Slack refusing
     * the install with `bad_redirect_uri`.
     */
    SlackIntegrationCallback: "/integrations/slack/callback",
    Invite: "/invites",
    /**
     * Completes a Slack account link. The path is baked into the URLs the backend mints and Slack
     * has already delivered, so changing it strands every link in flight.
     */
    LinkSlack: "/link-slack",
    Invocations: "/invocations",
    InvokeAction: "/actions/invoke",
    Metrics: "/metrics",
    Notifications: "/notifications",
    Orgs: "/orgs",
    RobotoUriRedirect: "/open",
    Search: "/search",
    Sessions: "/sessions",
    Settings: {
      Root: "/settings",
      Organization: "/settings/organization",
      Tokens: "/settings/tokens",
      Usage: "/settings/usage",
      AI: "/settings/ai",
      Secrets: "/settings/secrets",
      Integrations: "/settings/integrations",
    },
    SignIn: "/signin",
    SignUp: "/signup",
    Triggers: "/triggers",
    Unauthorized: "/unauthorized",
    Users: "/users",
    Visualize: "/visualize",
  } as const);

  #navigate: NavigateFunction;

  constructor(navigator: NavigateFunction) {
    this.#navigate = navigator;
  }

  /**
   * Navigate to an arbitrary path. Intended for use by the AI navigate tool
   * for routes that don't have a dedicated method.
   */
  public navigateTo(path: string) {
    this.#navigate(path);
  }

  public account() {
    this.#navigate(Goto.route.Account);
  }

  public home() {
    this.#navigate(Goto.route.Home);
  }

  public datasets() {
    this.#navigate(Goto.route.Datasets);
  }

  public devices() {
    this.#navigate(Goto.route.Devices);
  }

  public agents() {
    this.#navigate(Goto.route.Agents);
  }

  public comments() {
    this.#navigate(Goto.route.Comments);
  }

  public signIn(params: SignInRouteParams = {}) {
    const { inviteId, slackLinkNonce, chooseOrg } = params;

    if (inviteId && chooseOrg) {
      LoggerService.warn(
        "Both inviteId and chooseOrg are set. Ignoring chooseOrg",
      );
    }

    if (inviteId) {
      this.#navigate(`${Goto.route.SignIn}?inviteId=${inviteId}`);
      return;
    }

    // Carried as its own parameter rather than a general return-to URL: a nonce is opaque and can
    // only ever be interpolated into this one route, so nothing here has to be validated against
    // an allowlist the way an arbitrary destination would.
    if (slackLinkNonce) {
      this.#navigate(
        `${Goto.route.SignIn}?slackLinkNonce=${encodeURIComponent(slackLinkNonce)}`,
      );
      return;
    }

    if (chooseOrg) {
      this.#navigate(`${Goto.route.SignIn}?chooseorg=true`);

      return;
    }

    this.#navigate(Goto.route.SignIn);
  }

  public linkSlack(params: LinkSlackRouteParams) {
    this.#navigate(
      `${Goto.route.LinkSlack}/${encodeURIComponent(params.nonce)}`,
    );
  }

  public signUp(params: SignUpRouteParams = {}) {
    const { interruptedVerificationCode } = params;

    if (interruptedVerificationCode) {
      this.#navigate(`${Goto.route.SignUp}?interruptedVerificationCode=true`);
    } else {
      this.#navigate(Goto.route.SignUp);
    }
  }

  public invite(params: InviteRouteParams = {}) {
    const { inviteId } = params;
    if (inviteId) {
      this.#navigate(`${Goto.route.Invite}/${inviteId}`);
    } else {
      this.#navigate(Goto.route.Invite);
    }
  }

  public search(urlParams?: URLSearchParams) {
    if (urlParams) {
      this.#navigate(`${Goto.route.Search}?${urlParams.toString()}`);
      return;
    }
    this.#navigate(Goto.route.Search);
  }

  public static createVisualizerRoute(workspaceId?: string): string {
    return generatePath("/visualize/:workspaceId?", {
      workspaceId: workspaceId ?? null,
    });
  }

  /**
   * Skill detail page — where a skill is read and edited.
   *
   * Omitting `version` (or passing null) targets the skill itself, which
   * resolves to whatever its latest version is when the page loads. Passing
   * one pins that exact version, and the segment is spelled `v<N>` so the URL
   * reads the way versions are written everywhere else in the product.
   */
  public static createSkillRoute(
    skillId: string,
    version?: number | null,
  ): string {
    const base = `${Goto.route.AgentSkills}/${skillId}`;
    return version === undefined || version === null
      ? base
      : `${base}/versions/v${version}`;
  }

  /** Agent detail page (the editor, in its read-only view). */
  public static createAgentRoute(agentId: string): string {
    return `${Goto.route.Agents}/${agentId}`;
  }

  /** Threads tab of the agent detail UI — the table of threads launched
   *  from this agent. */
  public static createAgentThreadsRoute(agentId: string): string {
    return `${Goto.createAgentRoute(agentId)}/threads`;
  }

  /** Redirect route that forks ``threadId`` into a new private,
   *  caller-owned thread and opens it. Use this when building a shareable
   *  "fork this thread" link rather than hardcoding the ``/fork`` suffix. */
  public static createForkAgentThreadRoute(threadId: string): string {
    return `${Goto.route.AgentThreads}/${threadId}/fork`;
  }

  /** Launch form for an agent. ``datasetId`` pre-fills the agent's
   *  ``DATASET_ID`` variable, the handoff the dataset page's "Run agent"
   *  affordances use. */
  public static createAgentLaunchRoute(
    agentId: string,
    params: { datasetId?: string } = {},
  ): string {
    const base = `${Goto.createAgentRoute(agentId)}/launch`;
    return params.datasetId
      ? `${base}?dataset_id=${encodeURIComponent(params.datasetId)}`
      : base;
  }

  /**
   * The org-level metrics page: `/metrics`, or `/metrics/{dashboardId}` when a
   * saved dashboard is selected. The active saved-dashboard id is a route path
   * segment, never a query param; other dashboard view-state (`range`,
   * `period`, `tab`) stays in the query string and is appended by callers. A
   * `null`/`undefined` id means "no dashboard selected" and yields the bare
   * route.
   */
  public static createMetricsRoute(dashboardId?: string | null): string {
    return dashboardId
      ? `${Goto.route.Metrics}/${encodeURIComponent(dashboardId)}`
      : Goto.route.Metrics;
  }

  /**
   * A device's Metrics tab: `/devices/{deviceId}/metrics`, or
   * `/devices/{deviceId}/metrics/{dashboardId}` when a saved dashboard is
   * selected. A `null`/`undefined` id means "no dashboard selected" and yields
   * the bare tab.
   */
  public static createDeviceMetricsRoute(
    deviceId: string,
    dashboardId?: string | null,
  ): string {
    const base = `${Goto.route.Devices}/${encodeURIComponent(deviceId)}/metrics`;
    return dashboardId ? `${base}/${encodeURIComponent(dashboardId)}` : base;
  }

  /**
   * The metrics-dashboard route for one dashboard context: the device-scoped
   * Metrics tab when `deviceId` is set, the org-level page otherwise. This is
   * the builder `MetricsDashboard` itself navigates with when the active
   * dashboard changes — its `deviceId` prop is exactly the signal that
   * distinguishes its two routed mounts.
   */
  public static createMetricsDashboardRoute(opts: {
    deviceId?: string;
    dashboardId: string | null;
  }): string {
    return opts.deviceId
      ? Goto.createDeviceMetricsRoute(opts.deviceId, opts.dashboardId)
      : Goto.createMetricsRoute(opts.dashboardId);
  }

  public static createDatasetRoute(datasetId: string, directoryPath?: string) {
    if (directoryPath) {
      const encodedDirPath = directoryPath
        .split("/")
        .map(encodeURIComponent)
        .join("/");
      return `${Goto.route.Datasets}/${datasetId}/files/${encodedDirPath}`;
    }
    return `${Goto.route.Datasets}/${datasetId}`;
  }

  /**
   * Creates a redirect URL for a roboto:// URI.
   *
   * This is useful for generating shareable links that work outside the app
   * (e.g., in GitHub issues, Slack, JIRA) and redirect to the appropriate
   * page when clicked.
   *
   * @param robotoUri - A roboto:// URI (e.g., "roboto://dataset/ds_123")
   * @returns A relative URL path (e.g., "/r?uri=roboto%3A%2F%2Fdataset%2Fds_123")
   */
  public static createRedirectUrl(robotoUri: string): string {
    return `${Goto.route.RobotoUriRedirect}?uri=${encodeURIComponent(robotoUri)}`;
  }

  public dataset({ datasetId, directoryPath }: DatasetRouteParams) {
    const path = Goto.createDatasetRoute(datasetId, directoryPath);
    this.#navigate(path);
  }

  public datasetTopics({ datasetId }: DatasetRouteParams) {
    this.#navigate(`${Goto.route.Datasets}/${datasetId}/topics`);
  }

  public datasetEvents({ datasetId }: DatasetRouteParams) {
    this.#navigate(`${Goto.route.Datasets}/${datasetId}/events`);
  }

  public datasetActions({ datasetId }: DatasetRouteParams) {
    this.#navigate(`${Goto.route.Datasets}/${datasetId}/actions`);
  }

  public datasetDetails({ datasetId }: DatasetRouteParams) {
    this.#navigate(`${Goto.route.Datasets}/${datasetId}/details`);
  }

  public datasetPermissions({ datasetId }: DatasetRouteParams) {
    this.#navigate(`${Goto.route.Datasets}/${datasetId}/permissions`);
  }

  public createDataset() {
    this.#navigate(`${Goto.route.CreateDataset}`);
  }

  public createDatasetEvent({ datasetId }: DatasetRouteParams) {
    this.#navigate(`${Goto.route.Datasets}/${datasetId}/events/create`);
  }

  public actions(params: ActionsRouteParam = {}) {
    switch (params.tab) {
      case "hub":
        this.#navigate(Goto.route.ActionHub);
        break;
      case "triggers":
        this.#navigate(Goto.route.Triggers);
        break;
      case "invocations":
        this.#navigate(Goto.route.Invocations);
        break;
      case "images":
        this.#navigate(Goto.route.ActionImages);
        break;
      default:
        this.#navigate(Goto.route.Actions);
    }
  }

  public action({ action }: ActionRouteParams) {
    const urlParts: string[] = [Goto.route.Actions];
    if (action.owner) {
      urlParts.push(action.owner);
    }
    if (action.digest) {
      urlParts.push(`${action.name}?digest=${action.digest}`);
    } else {
      urlParts.push(action.name);
    }
    this.#navigate(urlParts.join("/"));
  }

  public createAction() {
    this.#navigate(Goto.route.CreateAction);
  }

  public invocation({ invocationId, tab }: InvocationRouteParams) {
    if (tab) {
      this.#navigate(`${Goto.route.Invocations}/${invocationId}?tab=${tab}`);
    } else {
      this.#navigate(`${Goto.route.Invocations}/${invocationId}`);
    }
  }

  public invokeAction(params: InvokeActionRouteParams = {}) {
    const { action, datasetId, baseInvocation, inputFiles } = params;
    const urlParams = new URLSearchParams();

    if (action) {
      // Add "action_digest" to query params when we want to
      // enable invoking a specific version of an action
      urlParams.append("action_name", action.name);
      if (action.owner) {
        urlParams.append("action_owner", action.owner);
      }
    }
    if (datasetId) {
      urlParams.append("dataset_id", datasetId);
    }
    if (baseInvocation) {
      urlParams.append("base_invocation", baseInvocation);
    }

    if (inputFiles) {
      const encodedInputFiles = inputFiles
        .map((item) => encodeURIComponent(item))
        .join(",");
      urlParams.append("input_files", encodedInputFiles);
    }

    const queryString = urlParams.toString();
    const href = queryString
      ? `${Goto.route.InvokeAction}?${urlParams.toString()}`
      : `${Goto.route.InvokeAction}`;

    this.#navigate(href);
  }

  public trigger({ triggerId }: TriggerRouteParams) {
    this.#navigate(`${Goto.route.Triggers}/${triggerId}`);
  }

  public createTrigger() {
    this.#navigate(Goto.route.CreateTrigger);
  }

  public collections() {
    this.#navigate(Goto.route.Collections);
  }

  public collection({ collectionId }: CollectionRouteParams) {
    this.#navigate(`${Goto.route.Collections}/${collectionId}`);
  }

  public device({ deviceId }: DeviceRouteParams) {
    this.#navigate(`${Goto.route.Devices}/${deviceId}`);
  }

  public agentThread(
    { threadId }: AgentThreadRouteParams,
    options?: { replace?: boolean },
  ) {
    // ``replace`` is for redirect routes (e.g. the fork-and-open route)
    // that should not leave their own URL in history — otherwise the
    // browser back button would land back on the redirect and re-run its
    // side effect (here, creating a second fork).
    this.#navigate(`${Goto.route.AgentThreads}/${threadId}`, options);
  }

  public session({ sessionId }: SessionRouteParams) {
    this.#navigate(`${Goto.route.Sessions}/${sessionId}`);
  }

  public sessions() {
    this.#navigate(Goto.route.Sessions);
  }

  /**
   * `datasetId` alone seeds the create page's file selection with every
   * file in that dataset. A pre-selection replaces that seed: `fileIds`
   * pre-seeds explicitly selected files and is valid with or without
   * `datasetId`, while `directoryPatterns` requires `datasetId` to scope
   * its expansion (see {@link CreateSessionRouteParams}).
   */
  public createSession(params: CreateSessionRouteParams = {}) {
    const { datasetId, fileIds, directoryPatterns } = params;
    const path = datasetId
      ? `${Goto.route.CreateSession}?dataset_id=${encodeURIComponent(datasetId)}`
      : Goto.route.CreateSession;
    const state: CreateSessionLocationState = {};
    if (fileIds && fileIds.length > 0) {
      state.fileIds = fileIds;
    }
    if (directoryPatterns && directoryPatterns.length > 0) {
      state.directoryPatterns = directoryPatterns;
    }
    this.#navigate(path, Object.keys(state).length > 0 ? { state } : undefined);
  }

  public file({ fileId, params }: FileRouteParams) {
    window.scrollTo(0, 0); // Ensure the page doesn't load scrolled down

    if (params) {
      const { layout, t } = params;
      const searchParams = new URLSearchParams();

      if (layout) {
        layoutNavigationState.set(layout);
      }

      if (t) {
        // The workspace expects the timestamp to be encoded as a fixed-point seconds value.
        searchParams.set(SearchParams.TIMESTAMP, nanoSecToSecStr(t));
      }

      const hasSearchParams = searchParams.size > 0;

      this.#navigate(
        `${Goto.route.Files}/${fileId}${hasSearchParams ? `?${searchParams}` : ""}`,
      );
    } else {
      this.#navigate(`${Goto.route.Files}/${fileId}`);
    }
  }

  public visualize({ dataSource, params, state }: VisualizerRouteParams) {
    const searchParams = new URLSearchParams();

    if (dataSource?.dataset) {
      searchParams.append(SearchParams.DATASET, dataSource.dataset);
    }

    if (dataSource?.files) {
      dataSource.files.forEach((fileId) => {
        searchParams.append(SearchParams.FILE, fileId);
      });
    }

    if (dataSource?.session) {
      searchParams.append(SearchParams.SESSION, dataSource.session);
    }

    if (params) {
      const { event, layout, t } = params;

      if (event) {
        searchParams.set(SearchParams.EVENT, event);
      }

      if (layout) {
        layoutNavigationState.set(layout);
      }

      if (t) {
        // The workspace expects the timestamp to be encoded as a fixed-point seconds value.
        searchParams.set(SearchParams.TIMESTAMP, nanoSecToSecStr(t));
      }
    }

    window.scrollTo(0, 0); // Ensure the page doesn't load scrolled down
    const hasSearchParams = searchParams.size > 0;
    this.#navigate(
      `${Goto.route.Visualize}${hasSearchParams ? `?${searchParams}` : ""}`,
      { state },
    );
  }

  public secretsList() {
    this.#navigate(Goto.route.Settings.Secrets);
  }
}

export type PageRoute = (typeof Goto.route)[keyof typeof Goto.route];

export type ExternalRoute = `https://${string}`;
